// Travel Tips — iter65 rebuild.
//
// 4-tab structure: In Cuba · Arriving · Leaving · Help.
// Spec: short scannable cards (max 3 sentences), gold-left-border tip cards,
// Help tab uses text-only emergency numbers (NO WhatsApp).
import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Phone, ChevronRight } from "lucide-react";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";
import SupportModal from "../components/SupportModal";

const TABS = ["in_cuba", "arriving", "leaving", "help"];

export default function Tips() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const initial = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "in_cuba";
  const [tab, setTab] = useState(initial);
  const [supportOpen, setSupportOpen] = useState(false);

  // iter138 — back-compat: old links use `/tips?tip=<id>`; redirect to the
  // dedicated article page so the body content renders on load.
  const legacyTipId = searchParams.get("tip");
  if (legacyTipId) {
    return <Navigate to={`/tips/${encodeURIComponent(legacyTipId)}`} replace />;
  }

  return (
    <main data-testid="tips-page" className="min-h-screen pb-32 bg-background">
      {/* Header */}
      <header className="px-5 pt-6 flex items-center gap-3">
        {/* iter174 — Back arrow routes to "/" which resolves to Ride Mode
         * or Discover home based on the user's current app mode. Previously
         * hardcoded to /journey, which forced Ride-mode users into the
         * Discover Journey page. */}
        <Link to="/" data-testid="tips-back" className="btn-ghost p-2" style={{ minHeight: 48, minWidth: 48 }} aria-label="Back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </Link>
        <div className="flex-1">
          <div className="eyebrow text-gold" style={{ letterSpacing: "0.18em" }}>{t("tips_eyebrow")}</div>
          <h1 className="font-display mt-1 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, lineHeight: 1.15 }}>
            {t("tips_title")}
          </h1>
        </div>
      </header>

      {/* Tab pills */}
      <div className="px-5 mt-6 flex gap-2 overflow-x-auto no-scrollbar" data-testid="tips-tabs">
        {TABS.map((key) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`tips-tab-${key}`}
              onClick={() => setTab(key)}
              className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-medium transition-colors whitespace-nowrap"
              style={{
                minHeight: 44,
                background: active ? "#E8B84B" : "#141414",
                color: active ? "#0B0B0B" : "#B0AAA3",
                border: active ? "1px solid transparent" : "1px solid rgba(232,184,75,0.35)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {t(`tips_tab_${key}`)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <section className="px-5 mt-6 space-y-3" data-testid={`tips-content-${tab}`}>
        {tab === "in_cuba" && <InCubaTab t={t} />}
        {tab === "arriving" && <ArrivingTab t={t} />}
        {tab === "leaving" && <LeavingTab t={t} />}
        {tab === "help" && <HelpTab t={t} onSupport={() => setSupportOpen(true)} />}
      </section>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
      <BottomNav />
    </main>
  );
}

/* ---------------- Reusable tip card ---------------- */
function TipCard({ title, body, testId }) {
  return (
    <article
      data-testid={testId}
      style={{
        background: "#141414",
        border: "0.5px solid #222",
        borderLeft: "3px solid #E8B84B",
        borderRadius: 14,
        padding: 16,
      }}
    >
      <h3
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 15,
          fontWeight: 500,
          color: "#F5F1E8",
          marginBottom: 6,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          color: "#B0AAA3",
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {body}
      </p>
    </article>
  );
}

function CtaButton({ to, onClick, label, testId }) {
  const baseStyle = {
    background: "transparent",
    border: "1px solid rgba(232,184,75,0.5)",
    color: "#E8B84B",
    borderRadius: 12,
    minHeight: 52,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "0.04em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "0 16px",
    width: "100%",
  };
  const className = "transition-colors hover:bg-[rgba(232,184,75,0.06)]";
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={className} style={baseStyle}>
        {label}
        <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={className} style={baseStyle}>
      {label}
      <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
}

/* ---------------- IN CUBA tab ---------------- */
function InCubaTab({ t }) {
  return (
    <>
      <TipCard testId="tip-currency" title={t("tip_currency_t")} body={t("tip_currency_b")} />
      <TipCard testId="tip-connectivity" title={t("tip_connectivity_t")} body={t("tip_connectivity_b")} />
      <TipCard testId="tip-safety" title={t("tip_safety_t")} body={t("tip_safety_b")} />
      <TipCard testId="tip-transport" title={t("tip_transport_t")} body={t("tip_transport_b")} />
    </>
  );
}

/* ---------------- ARRIVING tab ---------------- */
function ArrivingTab({ t }) {
  return (
    <>
      <TipCard testId="tip-arr-before" title={t("tip_arr_before_t")} body={t("tip_arr_before_b")} />
      <TipCard testId="tip-arr-airport" title={t("tip_arr_airport_t")} body={t("tip_arr_airport_b")} />
      <TipCard testId="tip-arr-ride" title={t("tip_arr_ride_t")} body={t("tip_arr_ride_b")} />
      <TipCard testId="tip-arr-terminal" title={t("tip_arr_terminal_t")} body={t("tip_arr_terminal_b")} />
      <div className="mt-4">
        <CtaButton to="/connected" label={t("tip_arr_esim_cta")} testId="tip-arr-esim-cta" />
      </div>
    </>
  );
}

/* ---------------- LEAVING tab ---------------- */
function LeavingTab({ t }) {
  return (
    <>
      <TipCard testId="tip-lv-timing" title={t("tip_lv_timing_t")} body={t("tip_lv_timing_b")} />
      <TipCard testId="tip-lv-goods" title={t("tip_lv_goods_t")} body={t("tip_lv_goods_b")} />
      <TipCard testId="tip-lv-currency" title={t("tip_lv_currency_t")} body={t("tip_lv_currency_b")} />
      <TipCard testId="tip-lv-airport" title={t("tip_lv_airport_t")} body={t("tip_lv_airport_b")} />
      <div className="mt-4">
        <CtaButton to="/airport" label={t("tip_lv_airport_cta")} testId="tip-lv-airport-cta" />
      </div>
    </>
  );
}

/* ---------------- HELP tab — strictly NO WhatsApp on emergencies ---------------- */
function HelpTab({ t, onSupport }) {
  const numbers = [
    { key: "police", label: t("tip_help_police"), num: "106" },
    { key: "ambulance", label: t("tip_help_ambulance"), num: "104" },
    { key: "fire", label: t("tip_help_fire"), num: "105" },
    { key: "tourist", label: t("tip_help_tourist"), num: "832-4488" },
  ];
  return (
    <>
      {/* Emergency numbers — TEXT ONLY, no WhatsApp */}
      <article
        data-testid="tip-help-emergency"
        style={{
          background: "#141414",
          border: "0.5px solid #222",
          borderLeft: "3px solid #E8B84B",
          borderRadius: 14,
          padding: 16,
        }}
      >
        <h3
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            fontWeight: 500,
            color: "#F5F1E8",
            marginBottom: 10,
          }}
        >
          {t("tip_help_emergency_t")}
        </h3>
        <ul className="space-y-2 mb-3">
          {numbers.map((n) => (
            <li key={n.key} className="flex items-center justify-between" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <span style={{ fontSize: 14, color: "#B0AAA3" }}>{n.label}</span>
              <span data-testid={`emergency-${n.key}`} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontVariantNumeric: "tabular-nums" }}>
                {n.num}
              </span>
            </li>
          ))}
        </ul>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#9A9490", lineHeight: 1.5, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
          <Phone className="w-3 h-3" strokeWidth={1.5} />
          {t("tip_help_emergency_note")}
        </p>
      </article>

      {/* Habano Support */}
      <article
        data-testid="tip-help-support"
        style={{
          background: "#141414",
          border: "0.5px solid #222",
          borderLeft: "3px solid #E8B84B",
          borderRadius: 14,
          padding: 16,
        }}
      >
        <h3
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            fontWeight: 500,
            color: "#F5F1E8",
            marginBottom: 6,
          }}
        >
          {t("tip_help_support_t")}
        </h3>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.5, margin: "0 0 12px 0" }}>
          {t("tip_help_support_b")}
        </p>
        <CtaButton onClick={onSupport} label={t("tip_help_support_cta")} testId="tip-help-support-cta" />
      </article>

      <TipCard testId="tip-help-lost" title={t("tip_help_lost_t")} body={t("tip_help_lost_b")} />
      <TipCard testId="tip-help-embassy" title={t("tip_help_embassy_t")} body={t("tip_help_embassy_b")} />
    </>
  );
}
